import * as React from 'react';
import ReactTable, { Column } from 'react-table';
import styled from '@emotion/styled';
import { guestVMsViewModel } from '../models/views/guestVMsViewModel';
import './../css/GuestVMs.css';
import ReactTablePagination from './ReactTablePagination';
import OSOtherIcon from '../icons/OSOtherIcon';
import OSWindowsIcon from '../icons/OSWindowsIcon';
import OSLinuxIcon from '../icons/OSLinuxIcon';

interface Props {
  data: guestVMsViewModel[];
}

const Header = styled.div`
  text-align: left;
  text-transform: none;
`;

const Cell = styled.div`
  text-align: right;
`;
const NameCell = styled.div`
  text-align: left;
`;
const IconCell = styled.div`
  text-align: center;
`;

// Default sort for React grid, extracted for convenience.
const customSort = (a: any, b: any, desc: boolean) => {
  // force null and undefined to the bottom
  a = a === null || a === undefined ? -Infinity : a;
  b = b === null || b === undefined ? -Infinity : b;
  // force any string values to lowercase
  a = typeof a === 'string' ? a.toLowerCase() : a;
  b = typeof b === 'string' ? b.toLowerCase() : b;
  // Return either 1 or -1 to indicate a sort priority
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  // returning 0 or undefined will use any subsequent
  // column sorting methods or the row index as a tiebreaker
  return 0;
};
function selectOSIcon(OSType: string) {
  if (OSType == '-') {
    return OSType;
  }
  return OSType == 'Linux' ? (
    <OSLinuxIcon />
  ) : OSType == 'Windows' ? (
    <OSWindowsIcon />
  ) : (
    <OSOtherIcon />
  );
}

class GuestVMsTableView extends React.Component<Props> {
  isRunningRow = {
    Header: <Header>IsRunning</Header>,
    id: 'IsRunning',
    minWidth: 100,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.IsRunning, b.IsRunning, false),
    Cell: row => <NameCell>{row.value.IsRunning}</NameCell>
  } as Column<guestVMsViewModel>;
  guestVMNameRow = {
    Header: <Header>Guest VM Name</Header>,
    id: 'VMName',
    minWidth: 200,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.VMName, b.VMName, false),
    Cell: row => <NameCell>{row.value.VMName}</NameCell>
  } as Column<guestVMsViewModel>;
  OSRow = {
    Header: <Header>Guest VM OS</Header>,
    id: 'OS',
    minWidth: 100,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.OS, b.OS, false),
    Cell: row => <IconCell>{selectOSIcon(row.value.OS)}</IconCell>
  } as Column<guestVMsViewModel>;
  diskCapacityRow = {
    Header: <Header>Guest VM Disk Capacity</Header>,
    id: 'DiskCapacity',
    minWidth: 150,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.DiskCapacity, b.DiskCapacity, false),
    Cell: row => <Cell>{row.value.DiskCapacity + ' GiB'}</Cell>
  } as Column<guestVMsViewModel>;
  diskUsedRow = {
    Header: <Header>Guest VM Disk Used</Header>,
    id: 'DiskUsed',
    minWidth: 150,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.DiskUsed, b.DiskUsed, false),
    Cell: row => <Cell>{row.value.DiskUsed + ' GiB'}</Cell>
  } as Column<guestVMsViewModel>;
  occupancyRow = {
    Header: <Header>Guest VM % Occupancy</Header>,
    id: 'DiskOccupancy',
    minWidth: 150,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.DiskOccupancy, b.DiskOccupancy, false),
    Cell: row => <Cell>{row.value.DiskOccupancy + ' %'}</Cell>
  } as Column<guestVMsViewModel>;
  usedMemoryRow = {
    Header: <Header>Used Memory (Active)</Header>,
    id: 'UsedMemory',
    minWidth: 150,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.UsedMemory, b.UsedMemory, false),
    Cell: row => <Cell>{row.value.UsedMemory + ' GiB'}</Cell>
  } as Column<guestVMsViewModel>;
  provisionedMemoryRow = {
    Header: <Header>Provisioned Memory</Header>,
    id: 'ProvisionedMemory',
    minWidth: 150,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.ProvisionedMemory, b.ProvisionedMemory, false),
    Cell: row => <Cell>{row.value.ProvisionedMemory + ' GiB'}</Cell>
  } as Column<guestVMsViewModel>;
  virtualCPURow = {
    Header: <Header>Virtual CPU</Header>,
    id: 'VirtualCPU',
    minWidth: 100,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.VirtualCPU, b.VirtualCPU, false),
    Cell: row => <Cell>{row.value.VirtualCPU}</Cell>
  } as Column<guestVMsViewModel>;
  platformRow = {
    Header: <Header>Platform</Header>,
    id: 'Platform',
    minWidth: 100,
    accessor: (d: guestVMsViewModel) => d,
    sortMethod: (a, b) => customSort(a.Platform, b.Platform, false),
    Cell: row => <Cell>{row.value.Platform}</Cell>
  } as Column<guestVMsViewModel>;

  render() {
    return (
      <ReactTable
        PaginationComponent={ReactTablePagination}
        showPaginationTop={true}
        showPaginationBottom={false}
        minRows={3}
        data={this.props.data}
        defaultPageSize={15}
        className="-striped -highlight"
        multiSort={false}
        noDataText="No data to display"
        columns={[
          this.isRunningRow,
          this.guestVMNameRow,
          this.OSRow,
          this.diskCapacityRow,
          this.diskUsedRow,
          this.occupancyRow,
          this.usedMemoryRow,
          this.provisionedMemoryRow,
          this.virtualCPURow,
          this.platformRow
        ]}
      />
    );
  }
}
export default GuestVMsTableView;
